import Popper from 'popper.js'

import HighlightTemplate from '../../html/components/highlight.html'

import Component from '../shared/component'
import Relation, {HasMany, MemberOf} from '../shared/mixins/relation'
import debounce from '../shared/debounce'

const highlightContext = Relation()
const textNodeContains = () => false

export const Highlight = Component('v-highlight', {
  inheritAttrs: false,
  mixins: [
    MemberOf(highlightContext, 'context')
  ],
  template: HighlightTemplate,
  props: {
    target: {
      description: `
        The target node to highlight. Can be either a CSS selector or a direct reference to a node.
      `,
      default: null,
      required: true
    },
    focused: {
      description: `
        Whether or not the highlight should be focused and scrolled into view.
      `,
      type: Boolean,
      default: false
    },
    label: String
  },
  data () {
    return {
      active: this.focused,
      scrolling: false,
      attachment: null,
      popper: null
    }
  },
  computed: {
    type () {
      const { target } = this

      if (!target) {
        return
      }

      if (typeof target === 'string') {
        return 'element'
      }

      switch (target.nodeType || false) {
        case target.ELEMENT_NODE:
          return 'element'
        case target.TEXT_NODE:
          return 'text'
        case target.ATTRIBUTE_NODE:
          return 'attribute'
      }
    },

    classes () {
      return {
        'is-focused': this.active && !this.scrolling,
        [`has-${this.type}`]: this.type
      }
    },

    style () {
      return this.popper && this.popper.data && this.popper.data.styles
    },

    bounds () {
      const { attachment } = this

      if (!attachment || !this.style) {
        return []
      }

      const bounds = attachment.getBoundingClientRect()

      switch (this.type || false) {
        case 'attribute':
        case 'element':
          return [{
            top: '0px',
            left: '0px',
            width: bounds.width + 'px',
            height: bounds.height + 'px'
          }]

        case 'text':
          const rects = [...attachment.getClientRects()]
          return rects.map(rect => ({
            top: (rect.top - bounds.top) + 'px',
            left: (rect.left - bounds.left) + 'px',
            width: rect.width + 'px',
            height: rect.height + 'px'
          }))
      }
    }
  },
  watch: {
    focused () {
      this.active = this.focused
    },

    active () {
      if (this.active) {
        this.$emit('focus')

        if (this.attachment) {
          this.$nextTick(this.scrollToAttachment)
        }
      } else {
        this.$emit('blur')
      }
    }
  },
  methods: {
    attach (attachment) {
      if (attachment.nodeType !== attachment.ELEMENT_NODE) {
        const range = document.createRange()
        range.selectNode(attachment)
        Object.assign(attachment, {
          getBoundingClientRect: range.getBoundingClientRect.bind(range),
          getClientRects: range.getClientRects.bind(range),
          contains: textNodeContains
        })
      }

      this.attachment = attachment

      const update = data => {
        this.$set(this.popper, 'data', {
          styles: {
            transform: data.styles.transform,
            position: data.styles.position,
            top: `${data.styles.top}px`,
            left: `${data.styles.left}px`,
            width: `${data.offsets.reference.width}px`,
            height: `${data.offsets.reference.height}px`
          }
        })
      }

      if (this.popper) {
        this.popper.destroy()
      }

      this.popper = new Popper(
        this.attachment,
        this.$el,
        {
          modifiers: {
            applyStyle: {
              enabled: false
            },
            flip: {
              enabled: false
            },
            shift: {
              enabled: false
            },
            preventOverflow: {
              enabled: false
            },
            hide: {
              enabled: false
            },
            inner: {
              enabled: true
            }
          },
          onCreate: update,
          onUpdate: update
        }
      )

      this.$nextTick(this.popper.scheduleUpdate)

      if (this.focused) {
        this.$nextTick(this.scrollToAttachment)
      }
    },

    scrollToAttachment () {
      const { attachment } = this

      if (attachment && !this.scrolling) {
        const target = attachment.nodeType !== attachment.ELEMENT_NODE
          ? attachment.parentNode
          : attachment

        this.scrolling = true

        target.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'nearest' })

        const scrollables = parents(target)

        const done = debounce(({ target } = {}) => {
          this.scrolling = false

          // Schedule a popper update once the scrolling has finished. This ensures that the popper is positioned
          // correctly even in some edge cases.
          if (this.popper) {
            this.popper.scheduleUpdate()
          }

          scrollables.forEach(scrollable => {
            scrollable.removeEventListener('scroll', done)
          })
        }, 250)

        scrollables.forEach(scrollable => {
          scrollable.addEventListener('scroll', done, { passive: true })
        })

        done()
      }
    }
  },
  mounted () {
    if (this.focused) {
      this.scrollToAttachment()
    }
  },
  destroyed () {
    if (this.popper) {
      this.popper.destroy()
    }
  }
})

export const HighlightContextMixin = {
  mixins: [
    HasMany(highlightContext, 'highlights')
  ],
  data () {
    return {
      index: new Map()
    }
  },
  watch: {
    highlights () {
      const index = new Map()

      this.highlights.forEach(highlight => {
        let {target, attach} = highlight

        if (!target) {
          return
        }

        switch (target.constructor) {
          case String:
            target = this.source.querySelector(target)
        }

        const options = {attach}

        if (index.has(target)) {
          index.get(target).push(options)
        } else {
          index.set(target, [options])
        }
      })

      this.index = index
    }
  }
}

function parents (element) {
  const parents = []

  while (element) {
    parents.push(element)

    if (element.tagName.toLowerCase() === 'html') {
      parents.push(element.parentNode.defaultView)
    }

    element = element.parentElement
  }

  return parents
}
